import { Button, FormControlLabel, Radio, RadioGroup } from "@mui/material"
import { isEqual } from "lodash"
import React, { ChangeEvent } from "react"
import styled, { ThemeProvider } from "styled-components"
import { theme } from "../../../theme"
import { AddressInfo } from "../address"
import { Popper } from "./Popper"
import { PopperDarkTheme, PopperLightTheme } from "./popperThemes"
import { RadioGroupPopperProps } from "./RadioGroupPopper"

const formatAddressString = (address: AddressInfo) => {
  const beforeSuite = `<div>${address.recipientName}</div>
  <div>${address.firstLineDescription}</div>`
  const suite = address.addressSuite ? `<div>${address.addressSuite}</div>` : ""
  const afterSuite = `<div>${address.secondLineDescription}</div>
  <div>${address.thirdLineDescription}</div>`

  return beforeSuite + suite + afterSuite
}

export type AddressRadioGroupPopperProps<TValue = any> = RadioGroupPopperProps<TValue> & {
  showCustomAddressButton?: boolean
  customAddressButtonLabel?: string
  onClickCustomAddress?: () => void
  equalityMethod?: (value: TValue | "" | null, option: TValue) => boolean
}

export const AddressRadioGroupPopper = (props: AddressRadioGroupPopperProps) => {
  const {
    popperTitle,
    name,
    value,
    onChange,
    onChangeAndClose,
    onClose,
    onDelete,
    options = [],
    className,
    lightMode,
    anchorEl,
    anchorOrigin,
    transformOrigin,
    formatOption = formatAddressString,
    showCustomAddressButton = false,
    customAddressButtonLabel,
    onClickCustomAddress = () => undefined,
    equalityMethod = isEqual,
    dataCy,
    formatOptionDataCy = (_) => "",
  } = props

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChangeAndClose) {
      onChangeAndClose(e.target.value)
      return
    }

    onChange && onChange(e.target.value)
  }

  return (
    <ThemeProvider theme={lightMode ? PopperLightTheme : PopperDarkTheme}>
      <Popper
        title={popperTitle}
        onClose={onClose}
        onDelete={onDelete}
        anchorEl={anchorEl}
        className={className}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <PopperRadioGroup name={name} value={value}>
          {options.map((option) => (
            <div key={option}>
              <OptionContainer
                onClick={() => {
                  onChangeAndClose && onChangeAndClose(option)
                }}
              >
                <FormControlLabel
                  control={
                    <Radio
                      name={name}
                      checked={equalityMethod(value, option)}
                      value={option}
                      onChange={handleChange}
                      data-cy={`${dataCy}-${option}`}
                    />
                  }
                  label={null}
                  data-cy={formatOptionDataCy(option)}
                />
                <div dangerouslySetInnerHTML={{ __html: formatOption(option) }} />
              </OptionContainer>
            </div>
          ))}
        </PopperRadioGroup>

        {showCustomAddressButton && (
          <CustomButton onClick={onClickCustomAddress}>{customAddressButtonLabel}</CustomButton>
        )}
      </Popper>
    </ThemeProvider>
  )
}

const OptionContainer = styled.div`
  display: flex;
  cursor: pointer;
  padding: ${theme.spacing.s} 0;
  align-items: flex-start;
`

const PopperRadioGroup = styled(RadioGroup)`
  & label {
    color: ${(p) => p.theme.color};

    & .MuiRadio-root:not(.Mui-error) {
      color: ${theme.color.grey2};
    }

    & .MuiRadio-root.Mui-checked:not(.Mui-error) {
      color: ${theme.color.white};
    }
  }
`

const CustomButton = styled(Button)`
  color: ${theme.color.white};
  text-transform: none;
  padding: ${theme.spacing.m};
  display: block;
`
